/**
 * Composant de chargement centralisé avec CircularProgress
 *
 * Affiche un loader circulaire parfaitement centré avec un message personnalisable.
 * Utilisé pour tous les états de chargement de configuration et de vérification
 * d'accès dans l'application.
 *
 * Supporte deux modes de positionnement :
 * - Absolu (par défaut) : Couvre tout l'écran, utilisé pour les chargements initiaux
 * - Relatif : S'intègre dans le layout existant, préserve le header/sidebar
 *
 * @module CircularLoader
 * @author Équipe Brydge
 * @since 3.2.0
 */

import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

/**
 * Composant de chargement avec CircularProgress centré
 *
 * Affiche un loader circulaire et un message de chargement. Le positionnement
 * peut être absolu (plein écran) ou relatif (dans le container parent).
 *
 * @param {Object} props - Props du composant
 * @param {string} [props.message='Chargement...'] - Message affiché sous le loader
 * @param {number} [props.size=40] - Taille du CircularProgress en pixels
 * @param {boolean} [props.absolute=true] - Position absolue (plein écran) ou relative (dans container)
 * @param {Object} [props.sx] - Styles MUI supplémentaires pour la Box container
 * @return {JSX.Element} Le composant CircularLoader
 *
 * @example
 * // Utilisation simple (plein écran)
 * <CircularLoader />
 *
 * @example
 * // Avec message personnalisé
 * <CircularLoader message="Vérification de la configuration wellness..." />
 *
 * @example
 * // Mode relatif (préserve le layout)
 * <CircularLoader message="Chargement..." absolute={false} />
 *
 * @example
 * // Avec taille personnalisée
 * <CircularLoader message="Chargement" size={50} />
 */
export default function CircularLoader({
  message = 'Chargement...',
  size = 40,
  absolute = true,
  sx,
  ...other
}) {
  return (
    <Box
      sx={{
        position: absolute ? 'absolute' : 'relative',
        ...(absolute ? {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.default',
          zIndex: 9998,
        } : {
          minHeight: 'calc(100vh - 80px)', // Hauteur viewport - header approximatif
          width: '100%',
        }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        ...sx,
      }}
      {...other}
    >
      <CircularProgress size={size} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

CircularLoader.propTypes = {
  message: PropTypes.string,
  size: PropTypes.number,
  absolute: PropTypes.bool,
  sx: PropTypes.object,
};
